import { FunctionComponent, useEffect, useRef, useState } from "react";
import { Button, Input, Spin, Typography } from "antd";
import { SendOutlined } from "@ant-design/icons";
import { useChatbot } from "./useChatbot";
import { ChatMessage } from "./ChatMessage";
import { getChatbotSuggestions } from "./chatbotSuggestions";
import { useAuth } from "../../hooks/AuthContext";
import { useChatbotStore } from "../stores/useChatbotStore";

const { Text, Paragraph } = Typography;

export const ChatbotPanel: FunctionComponent = () => {
    const { messages, loading, sendQuestion, setFeedback } = useChatbot();
    const { roles } = useAuth();
    const pendingQuestion = useChatbotStore((state) => state.pendingQuestion);
    const setPendingQuestion = useChatbotStore((state) => state.setPendingQuestion);
    const [question, setQuestion] = useState("");
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const suggestions = getChatbotSuggestions(roles);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
    }, [messages.length, loading]);

    // Question posée depuis une autre surface (bandeau de l'accueil) : on la vide AVANT
    // l'envoi, sinon un second rendu (StrictMode, changement de loading) la renverrait.
    // `sendQuestion` est volontairement hors des dépendances : son identité change à chaque
    // rendu, l'y mettre relancerait l'envoi en boucle.
    useEffect(() => {
        if (pendingQuestion && !loading) {
            setPendingQuestion(null);
            sendQuestion(pendingQuestion);
        }
    }, [pendingQuestion, loading, setPendingQuestion]);

    const handleSend = () => {
        const value = question;
        setQuestion("");
        sendQuestion(value);
    };

    return (
        <div className="chatbot-panel">
            <div className="chatbot-panel-messages">
                {messages.length === 0 && (
                    <div className="chatbot-welcome">
                        <Paragraph style={{ marginBottom: 4 }}>
                            <Text strong>Bonjour, je suis votre assistant.</Text>
                        </Paragraph>
                        <Paragraph type="secondary" style={{ marginBottom: 0 }}>
                            J'ai été conçu pour vous aider à trouver les informations qui vous permettront d'utiliser au mieux la plateforme.
                            Posez-moi votre question !
                        </Paragraph>
                        {suggestions.length > 0 && (
                            <div className="chatbot-suggestions">
                                <Text type="secondary" className="chatbot-suggestions-label">
                                    Par exemple :
                                </Text>
                                {suggestions.map((suggestion) => (
                                    <button
                                        key={suggestion}
                                        type="button"
                                        className="chatbot-suggestion"
                                        disabled={loading}
                                        onClick={() => sendQuestion(suggestion)}
                                    >
                                        {suggestion}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {messages.map((message) => (
                    <ChatMessage key={message.id} message={message} onFeedback={setFeedback} />
                ))}

                {loading && (
                    <div className="chatbot-message-row chatbot-message-row--assistant">
                        <div className="chatbot-message-bubble chatbot-message-bubble--assistant chatbot-message-bubble--loading">
                            <Spin size="small" />
                            <Text type="secondary">L'assistant rédige…</Text>
                        </div>
                    </div>
                )}

                <div ref={messagesEndRef} />
            </div>

            <div className="chatbot-panel-input">
                <Input
                    value={question}
                    onChange={(e) => setQuestion(e.target.value)}
                    onPressEnter={handleSend}
                    placeholder="Posez votre question..."
                    disabled={loading}
                    aria-label="Votre question pour l'assistant"
                />
                <Button
                    type="primary"
                    icon={<SendOutlined />}
                    onClick={handleSend}
                    disabled={!question.trim() || loading}
                    loading={loading}
                    aria-label="Envoyer la question"
                />
            </div>
        </div>
    );
};
